// TennisTrack Hub — Pannello Admin | 029 Project
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Admin.css';

// ──────────────────────────────────────────────
// Sezione: Dashboard statistiche
// ──────────────────────────────────────────────
const SezioneStats = ({ stats, loading }) => {
  if (loading) return <div className="loading-msg">Caricamento statistiche...</div>;
  if (!stats) return null;
  return (
    <div>
      <div className="admin-stats-grid">
        <div className="admin-stat">
          <span className="admin-stat-num">{stats.utenti?.totale || 0}</span>
          <span className="admin-stat-label">Utenti totali</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num" style={{ color: 'var(--color-primary)' }}>
            {stats.utenti?.premium || 0}
          </span>
          <span className="admin-stat-label">Utenti Premium</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num" style={{ color: 'var(--color-accent)' }}>
            {stats.atleti || 0}
          </span>
          <span className="admin-stat-label">Atleti attivi</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num" style={{ color: '#ffb347' }}>
            {stats.template?.in_revisione || 0}
          </span>
          <span className="admin-stat-label">In revisione</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num">{stats.template?.approvato || 0}</span>
          <span className="admin-stat-label">Template approvati</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat-num">{stats.abbonamenti_attivi || 0}</span>
          <span className="admin-stat-label">Abbonamenti attivi</span>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Revisione template
// ──────────────────────────────────────────────
const SezioneRevisioni = () => {
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalRifiuto, setModalRifiuto] = useState(null); // { id, titolo }
  const [notaRifiuto, setNotaRifiuto] = useState('');
  const [preview, setPreview] = useState(null); // { id, titolo, contenuto }
  const [actionLoading, setActionLoading] = useState(null);
  const [errore, setErrore] = useState('');

  const carica = useCallback(async () => {
    try {
      const res = await api.get('/admin/template/revisione');
      setTemplate(res.data.template);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel caricamento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const approva = async (id, piano = 'free') => {
    setActionLoading(id + '_approva');
    setErrore('');
    try {
      await api.patch(`/admin/template/${id}/approva`, { piano });
      await carica();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setActionLoading(null);
    }
  };

  const apriRifiuto = (t) => {
    setModalRifiuto(t);
    setNotaRifiuto('');
  };

  const confermaRifiuto = async () => {
    if (!notaRifiuto.trim()) return;
    setActionLoading(modalRifiuto.id + '_rifiuta');
    setErrore('');
    try {
      await api.patch(`/admin/template/${modalRifiuto.id}/rifiuta`, { nota: notaRifiuto });
      setModalRifiuto(null);
      await carica();
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  if (loading) return <div className="loading-msg">Caricamento...</div>;

  return (
    <div>
      {errore && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{errore}</div>}

      {template.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
          ✅ Nessun template in attesa di revisione.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="revisioni-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Tipo</th>
                <th>Autore</th>
                <th>Data invio</th>
                <th>Piano proposto</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {template.map(t => {
                const contenuto = typeof t.contenuto === 'string'
                  ? JSON.parse(t.contenuto || '{}')
                  : (t.contenuto || {});
                const nEsercizi = contenuto?.esercizi?.length || 0;

                return (
                  <tr key={t.id}>
                    <td>
                      <strong
                        style={{ color: 'var(--color-primary)', cursor: 'pointer' }}
                        onClick={() => setPreview(preview?.id === t.id ? null : { ...t, contenuto })}
                      >
                        {t.titolo}
                      </strong>
                      {t.descrizione && (
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                          {t.descrizione.substring(0, 80)}{t.descrizione.length > 80 ? '…' : ''}
                        </div>
                      )}
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {nEsercizi} esercizi · {t.livello}
                      </div>
                      {preview?.id === t.id && (
                        <div className="template-preview-panel">
                          {JSON.stringify(preview.contenuto, null, 2)}
                        </div>
                      )}
                    </td>
                    <td>
                      {t.tipo === 'tennis' ? '🎾' : '💪'} {t.tipo}
                    </td>
                    <td>
                      <div>{t.autore_nome} {t.autore_cognome}</div>
                      <div className="rev-autore">{t.autore_email}</div>
                      <div className="rev-autore">piano: {t.autore_piano}</div>
                    </td>
                    <td>{formatData(t.created_at)}</td>
                    <td>
                      <select
                        className="piano-select"
                        defaultValue="free"
                        id={`piano-${t.id}`}
                      >
                        <option value="demo">Demo</option>
                        <option value="free">Free</option>
                        <option value="premium">Premium</option>
                      </select>
                    </td>
                    <td>
                      <div className="rev-actions">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            const sel = document.getElementById(`piano-${t.id}`);
                            approva(t.id, sel?.value || 'free');
                          }}
                          disabled={actionLoading === t.id + '_approva'}
                        >
                          {actionLoading === t.id + '_approva' ? '...' : '✅ Approva'}
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => apriRifiuto(t)}
                          disabled={actionLoading === t.id + '_rifiuta'}
                        >
                          ❌ Rifiuta
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal rifiuto */}
      {modalRifiuto && (
        <div className="modal-overlay" onClick={() => setModalRifiuto(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>❌ Rifiuta template</h3>
            <p style={{ marginBottom: '12px', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Stai rifiutando: <strong style={{ color: 'var(--text-primary)' }}>{modalRifiuto.titolo}</strong>
            </p>
            <textarea
              placeholder="Motiva il rifiuto (obbligatorio) — sarà visibile all'autore nella notifica..."
              value={notaRifiuto}
              onChange={e => setNotaRifiuto(e.target.value)}
              maxLength={400}
            />
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModalRifiuto(null)}>Annulla</button>
              <button
                className="btn btn-danger"
                onClick={confermaRifiuto}
                disabled={!notaRifiuto.trim() || actionLoading}
              >
                {actionLoading ? '...' : '❌ Conferma rifiuto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Tutti i template
// ──────────────────────────────────────────────
const SezioneTuttiTemplate = () => {
  const [template, setTemplate] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroCerca, setFiltroCerca] = useState('');

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroStato) params.set('stato', filtroStato);
      if (filtroCerca) params.set('cerca', filtroCerca);
      const res = await api.get(`/admin/template?${params.toString()}`);
      setTemplate(res.data.template);
    } catch {
      /* silenzioso */
    } finally {
      setLoading(false);
    }
  }, [filtroStato, filtroCerca]);

  useEffect(() => { carica(); }, [carica]);

  const cambiaPiano = async (id, piano) => {
    try {
      await api.patch(`/admin/template/${id}/piano`, { piano });
      await carica();
    } catch {
      alert('Errore nel cambio piano.');
    }
  };

  const eliminaTemplate = async (id, titolo) => {
    if (!window.confirm(`Eliminare "${titolo}" dalla libreria? Azione irreversibile.`)) return;
    try {
      await api.delete(`/admin/template/${id}`);
      await carica();
    } catch {
      alert('Errore eliminazione.');
    }
  };

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <input
          type="text"
          className="form-input"
          placeholder="Cerca titolo..."
          value={filtroCerca}
          onChange={e => setFiltroCerca(e.target.value)}
          style={{ maxWidth: '220px' }}
        />
        <select className="form-select" value={filtroStato} onChange={e => setFiltroStato(e.target.value)} style={{ maxWidth: '160px' }}>
          <option value="">Tutti gli stati</option>
          <option value="bozza">Bozza</option>
          <option value="in_revisione">In revisione</option>
          <option value="approvato">Approvato</option>
          <option value="rifiutato">Rifiutato</option>
        </select>
      </div>

      {loading ? <div className="loading-msg">Caricamento...</div> : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="revisioni-table">
            <thead>
              <tr>
                <th>Titolo</th>
                <th>Tipo</th>
                <th>Autore</th>
                <th>Stato</th>
                <th>Piano</th>
                <th>Utilizzi</th>
                <th>Data</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {template.map(t => (
                <tr key={t.id}>
                  <td style={{ maxWidth: '180px' }}>
                    <strong style={{ fontSize: '0.88rem' }}>{t.titolo}</strong>
                  </td>
                  <td>{t.tipo === 'tennis' ? '🎾' : '💪'}</td>
                  <td>
                    <div style={{ fontSize: '0.82rem' }}>{t.autore_nome} {t.autore_cognome}</div>
                    <div className="rev-autore">{t.autore_email || '—'}</div>
                  </td>
                  <td>
                    <span className={`stato-badge stato-${t.stato}`}>{t.stato}</span>
                  </td>
                  <td>
                    <select
                      className="piano-select"
                      value={t.piano}
                      onChange={e => cambiaPiano(t.id, e.target.value)}
                    >
                      <option value="demo">Demo</option>
                      <option value="free">Free</option>
                      <option value="premium">Premium</option>
                    </select>
                  </td>
                  <td style={{ textAlign: 'center' }}>{t.n_utilizzi}</td>
                  <td>{formatData(t.created_at)}</td>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => eliminaTemplate(t.id, t.titolo)}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
              {template.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>Nessun template trovato.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Utenti
// ──────────────────────────────────────────────
const SezioneUtenti = () => {
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/utenti')
      .then(res => setUtenti(res.data.utenti))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatData = (iso) => iso
    ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  if (loading) return <div className="loading-msg">Caricamento...</div>;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <table className="utenti-table">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Email</th>
            <th>Piano</th>
            <th>Atleti</th>
            <th>Template</th>
            <th>Registrato</th>
          </tr>
        </thead>
        <tbody>
          {utenti.map(u => (
            <tr key={u.id}>
              <td>{u.nome} {u.cognome}</td>
              <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{u.email}</td>
              <td>
                <span className={`t-badge t-badge-${u.piano}`} style={{ textTransform: 'uppercase' }}>
                  {u.piano === 'premium' ? '⭐ ' : ''}{u.piano}
                </span>
              </td>
              <td style={{ textAlign: 'center' }}>{u.n_atleti}</td>
              <td style={{ textAlign: 'center' }}>{u.n_template}</td>
              <td>{formatData(u.created_at)}</td>
            </tr>
          ))}
          {utenti.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-secondary)' }}>Nessun utente.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ──────────────────────────────────────────────
// Sezione: Notifica globale
// ──────────────────────────────────────────────
const SezioneNotificaGlobale = () => {
  const [form, setForm] = useState({ titolo: '', messaggio: '', link: '', piano: '' });
  const [loading, setLoading] = useState(false);
  const [esito, setEsito] = useState('');
  const [errore, setErrore] = useState('');

  const invia = async (e) => {
    e.preventDefault();
    if (!form.titolo.trim() || !form.messaggio.trim()) return;
    setLoading(true);
    setEsito('');
    setErrore('');
    try {
      const res = await api.post('/admin/notifica-globale', form);
      setEsito(`✅ Notifica inviata a ${res.data.inviata_a} utenti.`);
      setForm({ titolo: '', messaggio: '', link: '', piano: '' });
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {esito && <div className="alert alert-success" style={{ marginBottom: '16px' }}>{esito}</div>}
      {errore && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{errore}</div>}

      <form className="card notifica-form" onSubmit={invia}>
        <div className="form-group">
          <label className="form-label">Titolo *</label>
          <input
            type="text"
            className="form-input"
            placeholder="Es. 🎉 Nuovi template disponibili!"
            value={form.titolo}
            onChange={e => setForm(f => ({ ...f, titolo: e.target.value }))}
            maxLength={200}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Messaggio *</label>
          <textarea
            className="form-textarea"
            placeholder="Testo della notifica..."
            value={form.messaggio}
            onChange={e => setForm(f => ({ ...f, messaggio: e.target.value }))}
            rows={3}
            maxLength={500}
            required
          />
        </div>
        <div className="form-group">
          <label className="form-label">Link interno (opzionale)</label>
          <input
            type="text"
            className="form-input"
            placeholder="Es. /template"
            value={form.link}
            onChange={e => setForm(f => ({ ...f, link: e.target.value }))}
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Invia solo al piano (vuoto = tutti)</label>
          <select
            className="form-select"
            value={form.piano}
            onChange={e => setForm(f => ({ ...f, piano: e.target.value }))}
          >
            <option value="">Tutti gli utenti</option>
            <option value="free">Solo Free</option>
            <option value="premium">Solo Premium</option>
            <option value="demo">Solo Demo</option>
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? '⏳ Invio...' : '📢 Invia notifica'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ──────────────────────────────────────────────
// Componente principale PannelloAdmin
// ──────────────────────────────────────────────
const SEZIONI = [
  { id: 'stats',     label: '📊 Dashboard',       icona: '📊' },
  { id: 'revisioni', label: '📋 Revisioni',        icona: '📋' },
  { id: 'template',  label: '📚 Tutti i template', icona: '📚' },
  { id: 'utenti',    label: '👥 Utenti',           icona: '👥' },
  { id: 'notifica',  label: '📢 Notifica globale', icona: '📢' },
];

const PannelloAdmin = () => {
  const navigate = useNavigate();
  const [sezione, setSezione] = useState('stats');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [accesso, setAccesso] = useState(true);

  useEffect(() => {
    api.get('/admin/stats')
      .then(res => setStats(res.data))
      .catch(err => {
        if (err.response?.status === 403) setAccesso(false);
      })
      .finally(() => setStatsLoading(false));
  }, []);

  if (!accesso) {
    return (
      <div className="page-layout">
        <Navbar />
        <main className="page-main" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ color: 'var(--text-primary)' }}>Area riservata</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>Non hai i permessi per accedere al pannello amministrativo.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        </main>
      </div>
    );
  }

  return (
    <div className="page-layout" style={{ flexDirection: 'column' }}>
      <Navbar />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

        {/* Sidebar */}
        <aside className="admin-sidebar">
          <div className="admin-sidebar-title">🛠 Admin 029 Project</div>
          {SEZIONI.map(s => (
            <div
              key={s.id}
              className={`admin-nav-link ${sezione === s.id ? 'active' : ''}`}
              onClick={() => setSezione(s.id)}
            >
              {s.label}
            </div>
          ))}
          <div style={{ marginTop: 'auto', padding: '16px', borderTop: '1px solid var(--border-color)' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/dashboard')}>
              ← App
            </button>
          </div>
        </aside>

        {/* Contenuto */}
        <main className="admin-content">
          <div className="admin-page-title">
            {SEZIONI.find(s => s.id === sezione)?.icona} {SEZIONI.find(s => s.id === sezione)?.label.replace(/^[^\s]+\s/, '')}
          </div>

          {sezione === 'stats'     && <SezioneStats stats={stats} loading={statsLoading} />}
          {sezione === 'revisioni' && <SezioneRevisioni />}
          {sezione === 'template'  && <SezioneTuttiTemplate />}
          {sezione === 'utenti'    && <SezioneUtenti />}
          {sezione === 'notifica'  && <SezioneNotificaGlobale />}
        </main>
      </div>
    </div>
  );
};

export default PannelloAdmin;
